import { useMemo } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { HoneymoonSearchLocation, HoneymoonSearchLocationUrlParams, TravelHoneymoonConfig } from './useTravelHoneymoonConfig.types';

const asFiniteNumber = (value: unknown): number | null => {
  // A blank string must NOT count: Number('') is 0, which would send the search to lat/lng 0,0.
  const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

// Zero and negatives are dropped rather than kept: "save up to $0" and "save up to -7%" are both
// worse than saying nothing, and every caller treats undefined as "quote no number".
const asPositiveNumber = (value: unknown): number | undefined => {
  const parsed = asFiniteNumber(value);
  return parsed !== null && parsed > 0 ? parsed : undefined;
};

// Zero is a real answer here — it turns a threshold off. A negative reads as unset.
const asNonNegativeNumber = (value: unknown): number | undefined => {
  const parsed = asFiniteNumber(value);
  return parsed !== null && parsed >= 0 ? parsed : undefined;
};

// A mistyped 150 would advertise "up to 150% off" — an offer no booking can honour. Out-of-range
// reads as unset, so the badge simply does not render.
const asPercentage = (value: unknown): number | undefined => {
  const parsed = asPositiveNumber(value);
  return parsed !== undefined && parsed <= 100 ? parsed : undefined;
};

export const parseHoneymoonLocationPayload = (raw: unknown): HoneymoonSearchLocation | null => {
  if (!raw || typeof raw !== 'object') return null;
  const payload = raw as Record<string, unknown>;

  const destination = typeof payload.destination === 'string' && payload.destination.trim() ? payload.destination.trim() : null;
  const lat = asFiniteNumber(payload.lat);
  const lng = asFiniteNumber(payload.lng);
  if (!destination || lat === null || lng === null) return null;

  const googlePlaceId = typeof payload.googlePlaceId === 'string' && payload.googlePlaceId.trim() ? payload.googlePlaceId.trim() : undefined;
  return { destination, lat, lng, ...(googlePlaceId && { googlePlaceId }) };
};

/**
 * Reads the whole flag payload into `TravelHoneymoonConfig`, falling back field by field.
 *
 * The location is accepted from `location` OR from the top level. The payload shipped flat before
 * this flag grew past one setting, so the flat read is what keeps a live flag working through the
 * rollout — drop it only once every environment sends the nested shape.
 */
export const parseTravelHoneymoonConfig = (raw: unknown): TravelHoneymoonConfig => {
  const payload = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};

  return {
    // No fallback destination on purpose: an unedited search must not run somewhere we picked.
    location: parseHoneymoonLocationPayload(payload.location) ?? parseHoneymoonLocationPayload(payload) ?? undefined,
    maxDiscountAmountUsdMajorUnits: asPositiveNumber(payload.maxDiscountAmountUsdMajorUnits),
    promoSliderMaxDiscountPercentage: asPercentage(payload.promoSliderMaxDiscountPercentage),
    showDialogMinFundBalanceMajorUnits: asPositiveNumber(payload.showDialogMinFundBalanceMajorUnits),
    minCouponSavingToShowMajorUnits: asNonNegativeNumber(payload.minCouponSavingToShowMajorUnits),
  };
};

/**
 * A honeymoon location as the hotel-search URL wants it.
 *
 * Exported because the couple's own pick has to produce byte-identical params to the configured
 * default — same keys, same stringification — or the two links would differ in shape and only one
 * of them would be exercised by a spec.
 */
export const toHoneymoonSearchLocationParams = (location: HoneymoonSearchLocation): HoneymoonSearchLocationUrlParams => ({
  destination: location.destination,
  lat: String(location.lat),
  lng: String(location.lng),
  ...(location.googlePlaceId && { googlePlaceId: location.googlePlaceId }),
});

type UseTravelHoneymoonConfigArgs = Readonly<{
  /** Pass true until the cash-fund surface is confirmed live, so the flag never loads (no Amplitude exposure) outside it. */
  skip: boolean;
}>;

type UseTravelHoneymoonConfigResult = Readonly<{
  config: TravelHoneymoonConfig;
  /** True while the payload is still loading — until then `config` holds defaults, not the configured values. */
  isLoading: boolean;
}>;

/**
 * Every honeymoon travel setting that lives in a flag payload, in one place.
 *
 * One flag rather than one per setting: these values are tuned together by marketing, and a couple
 * must never see a discount figure from one rollout beside a threshold from another.
 */
export const useTravelHoneymoonConfig = ({ skip }: UseTravelHoneymoonConfigArgs): UseTravelHoneymoonConfigResult => {
  const [, isConfigFlagLoading, , configFlagPayload] = useIsFeatureEnabled('travelHoneymoonConfig', { skip });

  const config = useMemo(() => parseTravelHoneymoonConfig(configFlagPayload), [configFlagPayload]);

  return {
    config,
    isLoading: !skip && isConfigFlagLoading,
  };
};
